import { Suspense, useRef, useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { PerspectiveCamera } from '@react-three/drei';
import { EffectComposer, Bloom, Vignette } from '@react-three/postprocessing';
import * as THREE from 'three';
import { ParticleField, ParticleFieldSecondary } from './ParticleField';

interface ImmersiveBackgroundProps {
  children: React.ReactNode;
  variant?: 'default' | 'hero' | 'dashboard' | 'minimal';
  intensity?: 'low' | 'medium' | 'high';
}

function SceneContent({
  intensity,
  mousePosition,
}: {
  intensity: string;
  mousePosition: { x: number; y: number };
}) {
  const cameraRef = useRef<THREE.PerspectiveCamera>(null);

  // Camera subtle movement based on mouse
  useEffect(() => {
    if (cameraRef.current) {
      cameraRef.current.position.x = mousePosition.x * 0.5;
      cameraRef.current.position.y = mousePosition.y * 0.3 + 1;
      cameraRef.current.lookAt(0, 0, 0);
    }
  }, [mousePosition]);

  const particleCount = intensity === 'high' ? 3000 : intensity === 'medium' ? 2000 : 1200;

  return (
    <>
      <PerspectiveCamera
        ref={cameraRef}
        makeDefault
        position={[0, 1, 12]}
        fov={60}
      />

      {/* Ambient lighting */}
      <ambientLight intensity={0.2} />
      <pointLight position={[10, 10, 10]} intensity={0.3} color="#B08080" />
      <pointLight position={[-10, -10, -10]} intensity={0.2} color="#B08080" />

      {/* Main particle field */}
      <ParticleField
        count={particleCount}
        color="#B08080"
        size={0.02}
        spread={20}
        speed={0.15}
        mouseInfluence={0.6}
      />

      {/* Secondary particle layer for depth */}
      <ParticleFieldSecondary count={Math.floor(particleCount * 0.3)} color="#888888" />

      {/* Post-processing effects */}
      <EffectComposer>
        <Bloom
          luminanceThreshold={0.7}
          luminanceSmoothing={0.9}
          intensity={0.3}
        />
        <Vignette eskil={false} offset={0.1} darkness={0.4} />
      </EffectComposer>
    </>
  );
}

export function ImmersiveBackground({
  children,
  variant = 'default',
  intensity = 'medium',
}: ImmersiveBackgroundProps) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Adjust intensity based on variant
  const effectiveIntensity = variant === 'hero' ? 'high' : variant === 'minimal' ? 'low' : intensity;

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: -(e.clientY / window.innerHeight) * 2 + 1,
      });
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <div className="relative min-h-screen">
      {/* 3D Canvas Background */}
      <div className="fixed inset-0 -z-10">
        <Canvas
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
          }}
          dpr={[1, 1.5]}
          style={{ background: 'linear-gradient(180deg, #000000 0%, #000000 50%, #000000 100%)' }}
        >
          <Suspense fallback={null}>
            <SceneContent
              intensity={effectiveIntensity}
              mousePosition={mousePosition}
            />
          </Suspense>
        </Canvas>
      </div>

      {/* Content overlay */}
      <div className="relative z-10">{children}</div>
    </div>
  );
}

// Simpler version for pages that need less visual complexity
export function MinimalBackground({ children }: { children: React.ReactNode }) {
  return <ImmersiveBackground variant="minimal" intensity="low">{children}</ImmersiveBackground>;
}

// Hero version for landing pages
export function HeroBackground({ children }: { children: React.ReactNode }) {
  return <ImmersiveBackground variant="hero" intensity="high">{children}</ImmersiveBackground>;
}

// Dashboard version with balanced elements
export function DashboardBackground({ children }: { children: React.ReactNode }) {
  return <ImmersiveBackground variant="dashboard" intensity="medium">{children}</ImmersiveBackground>;
}
